/**
 * Admin → Guide (STAFF_GUIDE_PLAN.md): the staff handbook for the reader's desk staff role. The API
 * sends only their articles, already filled from the live settings. Sections and search on the left,
 * the article on the right; on phones the list, then the article. `?tab=guide&a=<id>` opens one.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { articleTexts, fmtDateTime, GUIDE_ROLE_LABEL, guideInline, guideParts, type GuideArticle, type GuideBlock, type StaffGuide } from '@platform/shared';
import { cx, Empty, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const input = 'h-9 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';

const articleParam = () => new URLSearchParams(window.location.search).get('a');

function Inline({ text, titles, open }: { text: string; titles: Map<string, string>; open: (id: string) => void }) {
  return (
    <>
      {guideInline(text).map((p, i) => {
        if (p.type === 'bold') return <strong key={i}>{p.text}</strong>;
        if (p.type === 'italic') return <em key={i}>{p.text}</em>;
        if (p.type === 'code') return <code key={i} className="num rounded bg-raised px-1 py-0.5 text-[12px]">{p.text}</code>;
        if (p.type === 'see') {
          const title = titles.get(p.id);
          // A link to an article outside the reader's role: its title isn't sent, so it reads as plain text.
          return title ? (
            <button key={i} type="button" onClick={() => open(p.id)} className="font-medium text-accent hover:underline">
              {title}
            </button>
          ) : (
            <span key={i} className="text-muted">
              another article
            </span>
          );
        }
        return <span key={i}>{p.text}</span>;
      })}
    </>
  );
}

function Block({ b, titles, open }: { b: GuideBlock; titles: Map<string, string>; open: (id: string) => void }) {
  const line = (t: string) => <Inline text={t} titles={titles} open={open} />;
  if (b.type === 'table')
    return (
      <div className="overflow-x-auto rounded-xl border border-line">
        <table className="w-full min-w-[480px] border-collapse text-[13px]">
          <thead>
            <tr className="bg-panel text-left text-[11px] tracking-wide text-muted uppercase">
              {b.head.map((h) => (
                <th key={h} className="px-3 py-2 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((r, i) => (
              <tr key={i} className="border-t border-line">
                {r.map((c, j) => (
                  <td key={j} className={cx('px-3 py-2 align-top', j === 0 && 'font-medium')}>
                    {line(c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  if (b.type === 'callout')
    return <div className={cx('rounded-xl border px-4 py-3 text-[14px] leading-relaxed', b.tone === 'warn' ? 'border-warn/40 bg-warn-bg' : 'border-line bg-panel')}>{line(b.text)}</div>;
  return (
    <>
      {guideParts(b.text).map((p, i) => {
        if (p.type === 'h')
          return (
            <h3 key={i} className="m-0 pt-2 text-[15px] font-semibold">
              {line(p.text)}
            </h3>
          );
        if (p.type === 'list' || p.type === 'steps') {
          const List = p.type === 'list' ? 'ul' : 'ol';
          return (
            <List key={i} className={cx('m-0 flex flex-col gap-1 pl-5 text-[14px] leading-relaxed', p.type === 'list' ? 'list-disc' : 'list-decimal')}>
              {p.items.map((t, j) => (
                <li key={j}>{line(t)}</li>
              ))}
            </List>
          );
        }
        if (p.type === 'screenshot' && p.src)
          return (
            <figure key={i} className="m-0 flex flex-col gap-1.5">
              <a href={p.src} target="_blank" rel="noopener noreferrer" title="Open full size" className="block overflow-hidden rounded-xl border border-line bg-panel">
                <img src={p.src} alt={p.caption} loading="lazy" decoding="async" className="block h-auto w-full" />
              </a>
              <figcaption className="text-[13px] text-muted">{p.caption}</figcaption>
            </figure>
          );
        if (p.type === 'screenshot')
          return (
            <figure key={i} className="m-0 grid min-h-[160px] place-items-center rounded-xl border-2 border-dashed border-line-strong px-4 py-6 text-center">
              <figcaption className="text-[13px] text-muted">
                <span className="block text-[11px] tracking-wide uppercase">Screenshot</span>
                {p.caption}
              </figcaption>
            </figure>
          );
        return (
          <p key={i} className="m-0 text-[14px] leading-relaxed">
            {line(p.text)}
          </p>
        );
      })}
    </>
  );
}

function Article({ a, titles, open, back }: { a: GuideArticle; titles: Map<string, string>; open: (id: string) => void; back: ReactNode }) {
  return (
    <article className="flex max-w-[760px] flex-col gap-3">
      {back}
      <h2 className="m-0 text-[20px] font-semibold">{a.title}</h2>
      <p className="m-0 text-[14px] text-muted">{a.summary}</p>
      {a.body.map((b, i) => (
        <Block key={i} b={b} titles={titles} open={open} />
      ))}
    </article>
  );
}

export function GuideTab() {
  const [data, setData] = useState<StaffGuide | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [term, setTerm] = useState('');
  const [sel, setSel] = useState<string | null>(articleParam);
  useEffect(() => {
    api
      .get<StaffGuide>('/api/admin/guide')
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load the guide'));
  }, []);
  const titles = useMemo(() => new Map((data?.articles ?? []).map((a) => [a.id, a.title])), [data]);
  const found = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!data || !t) return data?.articles ?? [];
    return data.articles.filter((a) => articleTexts(a).some((x) => x.toLowerCase().includes(t)));
  }, [data, term]);
  if (error) return <p className="text-down">{error}</p>;
  if (!data)
    return (
      <div className="grid min-h-[200px] place-items-center text-muted">
        <Spinner />
      </div>
    );

  const open = (id: string | null) => {
    setSel(id);
    window.history.replaceState({}, '', id ? `/admin?tab=guide&a=${encodeURIComponent(id)}` : '/admin?tab=guide');
    document.getElementById('admin-main')?.scrollTo({ top: 0 });
  };
  const picked = data.articles.find((a) => a.id === sel) ?? null;
  // On a wide screen an article is always shown: the first one until another is picked.
  const shown = picked ?? data.articles[0] ?? null;

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-[13px] text-muted">
        Articles for your staff role, {GUIDE_ROLE_LABEL[data.role]}. Last checked against live settings: <span className="num">{fmtDateTime(data.asOf)}</span>
      </p>
      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <nav aria-label="Guide articles" className={cx('flex-col gap-4', picked ? 'hidden lg:flex' : 'flex')}>
          <input type="search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search the guide" className={cx(input, 'w-full')} aria-label="Search the guide" />
          {data.sections.map((s) => {
            const items = found.filter((a) => a.section === s.id);
            if (!items.length) return null;
            return (
              <div key={s.id} className="flex flex-col gap-0.5">
                <span className="px-2.5 pb-1 text-[13px] text-muted">{s.title}</span>
                {items.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    aria-current={shown?.id === a.id ? 'page' : undefined}
                    onClick={() => open(a.id)}
                    className={cx('flex flex-col rounded-[10px] px-2.5 py-2 text-left', shown?.id === a.id ? 'lg:bg-[#e8e8e8]' : 'hover:bg-[#efefef]')}
                  >
                    <span className="text-[14px] font-medium">{a.title}</span>
                    <span className="text-[12px] text-muted lg:hidden">{a.summary}</span>
                  </button>
                ))}
              </div>
            );
          })}
          {!found.length && <Empty>No article matches “{term.trim()}”.</Empty>}
        </nav>
        <div className={cx(picked ? 'block' : 'hidden lg:block')}>
          {shown ? (
            <Article
              a={shown}
              titles={titles}
              open={open}
              back={
                <button type="button" onClick={() => open(null)} className="h-9 self-start rounded-full px-1 text-[14px] font-medium text-muted hover:text-fg lg:hidden">
                  ‹ All articles
                </button>
              }
            />
          ) : (
            <Empty>No articles for your role yet.</Empty>
          )}
        </div>
      </div>
    </div>
  );
}
